import {
  calculateUsdAmount,
  encryptApiKey,
  sanitizeInput,
  formatProductTag,
} from "../Helper";
import { t } from "../../../../utils/i18n";
import { formErrors } from "../../../../utils/formErrors";
import { postRequest, putRequest } from "../../../../utils/httpRequest";

const useFormSubmission = () => {
  const handleSubmit = async ({
    formData,
    integration,
    storeId,
    walletAddress,
    exchangeRates,
    showSuccessModal,
    validateForm,
    setCreating,
    setErrors,
  }) => {
    setCreating(true);

    // Skip validation when editing
    if (!integration || !integration.id) {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setCreating(false);
        const firstErrorField = document.querySelector(
          ".form-input.error, .form-select.error"
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          if (typeof firstErrorField.focus === "function") {
            firstErrorField.focus({ preventScroll: true });
          }
        }
        return;
      }
    }

    console.log("Form submission started", {
      isEditing: !!(integration && integration.id),
      gohighlevel_enabled: formData.gohighlevel_enabled,
      hasNewApiKey: !!formData.gohighlevel_api_key,
    });

    try {
      // Parse existing data once at the beginning if editing
      let existingData = {};
      if (integration && integration.id) {
        try {
          if (
            integration.description &&
            integration.description.startsWith("[") &&
            integration.description.endsWith("]")
          ) {
            existingData = JSON.parse(
              integration.description.replace(/^\[|\]$/g, "")
            );
            console.log("Existing integration data:", {
              hasGhlApiKey: !!existingData.gohighlevel_api_key,
              gohighlevel_enabled: existingData.gohighlevel_enabled,
            });
          }
        } catch (e) {
          console.error("Error parsing existing integration data:", e);
        }
      }

      // Determine the API key to use
      let apiKeyToUse = "";
      
      if (formData.gohighlevel_enabled) {
        if (formData.gohighlevel_api_key && formData.gohighlevel_api_key !== "dummy-preserved-key") {
          // User provided a new API key - encrypt it
          try {
            apiKeyToUse = await encryptApiKey(formData.gohighlevel_api_key);
            console.log("New API key encrypted successfully");
          } catch (error) {
            console.error("API key encryption failed:", error);
            setErrors((prev) => ({
              ...prev,
              gohighlevel_api_key:
                t("store_integrations.widget.encrypt_failed"),
            }));
            setCreating(false);
            return;
          }
        } else if (existingData.gohighlevel_api_key) {
          // Editing mode - preserve existing encrypted key
          apiKeyToUse = existingData.gohighlevel_api_key;
          console.log("Preserving existing encrypted API key");
        }
      }

      console.log("API key decision:", {
        gohighlevel_enabled: formData.gohighlevel_enabled,
        hasNewKey: !!(formData.gohighlevel_api_key && formData.gohighlevel_api_key !== "dummy-preserved-key"),
        hasExistingKey: !!existingData.gohighlevel_api_key,
        willUseKey: !!apiKeyToUse,
      });

      const originalAmount = parseFloat(formData.amount);
      const originalCurrency = formData.currency;
      let payButtonUsdAmount;

      if (originalCurrency === "USD") {
        payButtonUsdAmount = originalAmount;
      } else {
        if (
          formData.amount &&
          !isNaN(originalAmount) &&
          exchangeRates &&
          exchangeRates[originalCurrency]
        ) {
          const usdEquivalent = calculateUsdAmount(
            originalAmount,
            originalCurrency,
            exchangeRates
          );
          const numericUsdEquivalent = parseFloat(usdEquivalent);

          if (!isNaN(numericUsdEquivalent)) {
            payButtonUsdAmount = numericUsdEquivalent;
          } else {
            console.warn(
              `USD conversion for ${originalCurrency} resulted in NaN. Defaulting pay_button_usd_amount to original amount.`
            );
            payButtonUsdAmount = originalAmount; // Fallback to original amount if conversion yields NaN
          }
        } else {
          console.warn(
            `Exchange rate for ${originalCurrency} not available or amount invalid. Defaulting pay_button_usd_amount to original amount.`
          );
          payButtonUsdAmount = originalAmount; // Fallback to original amount if rate is unavailable
        }
      }

      // Get CSRF token first
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      // Generate and shorten the payment URL
      let shortUrl = null;
      let existingShortUrlId = null;

      // If editing, check for existing short_url to get the ID for updating
      if (integration && integration.id && existingData.short_url) {
        // Extract the ID from the shortened URL (e.g., from "https://short.ly/OWEGGw" get "OWEGGw")
        const urlParts = existingData.short_url.split("/");
        existingShortUrlId = urlParts[urlParts.length - 1];
      }

      try {
        // Build the payment URL
        const baseUrl = "https://payment.bananacrystal.com/pay";
        const params = {
          store_id: storeId,
          amount: originalAmount,
          currency: originalCurrency,
          description: sanitizeInput(formData.description) || "Payment Widget",
          product_name: sanitizeInput(formData.product_name),
          redirect_url: formData.redirect_url,
          usd_amount: payButtonUsdAmount,
          gohighlevel_enabled: formData.gohighlevel_enabled,
          gohighlevel_api_key: apiKeyToUse,
          gohighlevel_tags: formData.gohighlevel_tags?.join(","),
        };

        console.log("Building payment URL with params:", {
          gohighlevel_enabled: params.gohighlevel_enabled,
          hasApiKey: !!params.gohighlevel_api_key,
          hasTags: !!params.gohighlevel_tags,
        });

        // Remove undefined/null values
        const cleanParams = Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v != null && v !== "")
        );

        const query = new URLSearchParams(cleanParams).toString();
        const fullPaymentUrl = `${baseUrl}?${query}`;

        // Call URL shortening API - use PUT if updating existing, POST if creating new
        let shortenResponse;
        if (existingShortUrlId) {
          // Update existing shortened URL
          shortenResponse = await fetch(
            `/api/v1/shorten_urls/${existingShortUrlId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": csrfToken,
              },
              body: JSON.stringify({
                original_url: fullPaymentUrl,
              }),
            }
          );
        } else {
          // Create new shortened URL
          shortenResponse = await fetch("/api/v1/shorten_urls", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({
              original_url: fullPaymentUrl,
            }),
          });
        }

        if (shortenResponse.ok) {
          const shortenData = await shortenResponse.json();
          shortUrl = shortenData.shortened_url || shortenData.short_url;
          console.log(
            `URL ${existingShortUrlId ? "updated" : "shortened"} successfully:`,
            shortUrl
          );
        } else {
          console.warn(
            "URL shortening failed, proceeding without shortened URL"
          );
        }
      } catch (error) {
        console.warn(
          "Error shortening URL:",
          error,
          "proceeding without shortened URL"
        );
      }

      let widgetData;
      let dataToSubmit;
      let encodedData;

      if (integration && integration.id) {
        // EDITING MODE: Update fields while preserving the API key
        widgetData = {
          ...existingData, // Start with all existing data
          description: sanitizeInput(formData.description) || "Payment Widget",
          product_name: sanitizeInput(formData.product_name),
          pay_button_note: sanitizeInput(formData.pay_button_note),
          pay_button_cta: sanitizeInput(formData.pay_button_cta),
          gohighlevel_enabled: formData.gohighlevel_enabled,
          gohighlevel_api_key: apiKeyToUse, // Use the determined API key
          gohighlevel_tags: formData.gohighlevel_tags?.join(","),
          pay_button_usd_amount: payButtonUsdAmount,
          short_url: shortUrl || existingData.short_url, // Preserve if shortening failed
        };

        console.log("Widget data for update:", {
          gohighlevel_enabled: widgetData.gohighlevel_enabled,
          hasApiKey: !!widgetData.gohighlevel_api_key,
          apiKeyLength: widgetData.gohighlevel_api_key?.length,
        });

        encodedData = `[${JSON.stringify(widgetData)}]`;

        dataToSubmit = {
          store_integration: {
            description: encodedData,
            pay_button_amount: originalAmount,
            pay_button_currency: originalCurrency,
            pay_button_cta: sanitizeInput(formData.pay_button_cta),
            integration_type: "Payment Widget",
            redirect_url: formData.redirect_url,
          },
        };
      } else {
        // CREATE MODE: New widget with all fields
        widgetData = {
          description: sanitizeInput(formData.description) || "Payment Widget",
          product_name: sanitizeInput(formData.product_name),
          pay_button_note: sanitizeInput(formData.pay_button_note),
          pay_button_cta: sanitizeInput(formData.pay_button_cta),
          integration_type: "Payment Widget",
          gohighlevel_enabled: formData.gohighlevel_enabled,
          gohighlevel_api_key: apiKeyToUse,
          gohighlevel_tags: formData.gohighlevel_tags?.join(","),
          pay_button_usd_amount: payButtonUsdAmount,
          short_url: shortUrl,
        };

        console.log("Widget data for creation:", {
          gohighlevel_enabled: widgetData.gohighlevel_enabled,
          hasApiKey: !!widgetData.gohighlevel_api_key,
        });

        encodedData = `[${JSON.stringify(widgetData)}]`;

        dataToSubmit = {
          store_integration: {
            description: encodedData,
            pay_button_amount: originalAmount,
            pay_button_currency: originalCurrency,
            pay_button_cta: sanitizeInput(formData.pay_button_cta),
            integration_type: "Payment Widget",
            redirect_url: formData.redirect_url,
          },
        };
      }

      if (integration && integration.id) {
        console.log(
          "Attempting PUT request to update integration...",
          dataToSubmit
        );
        await putRequest(
          `/stores/${storeId}/integrations/${integration.id}`,
          dataToSubmit,
          { "X-CSRF-Token": csrfToken }
        );
        console.log("PUT request presumed successful.");
        if (showSuccessModal) showSuccessModal();
        setTimeout(() => window.history.back(), 2000);
      } else {
        console.log(
          "Attempting POST request to create integration...",
          dataToSubmit
        );
        await postRequest(`/stores/${storeId}/integrations`, dataToSubmit, {
          "X-CSRF-Token": csrfToken,
        });
        console.log("POST request presumed successful.");
        setTimeout(() => window.history.back(), 1000);
      }
    } catch (error) {
      console.error("Form submission error in catch block:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
      }
      const formattedErrors = formErrors(error.response?.data?.errors || {});
      setErrors((prev) => ({
        ...prev,
        ...formattedErrors,
        submit:
          t("store_integrations.widget.submit_error"),
      }));
    } finally {
      console.log("Finally block: Setting creating to false.");
      setCreating(false);
    }
  };

  return { handleSubmit };
};

export default useFormSubmission;
